import { useMemo } from 'react';
import { useFinance } from '@/contexts/FinanceContext';
import type { FinanceTransaction } from '@/types/finance';
import { KIND_MAPPING, type ReportFilters, type ReportKind } from './types';
import { filterTransactionsForReport, summarizeRows } from './reportFilters';

export interface ReportRow extends FinanceTransaction {
  accountName: string;
  categoryName: string;
  costCenterName?: string;
  counterpartyName?: string;
}

export interface ReportData {
  rows: ReportRow[];
  totalAmount: number;
  count: number;
  averageAmount: number;
}

export function useReportData(kind: ReportKind, filters: ReportFilters): ReportData {
  const { transactions, accounts, categories, costCenters, counterparties } = useFinance();

  return useMemo(() => {
    const mapping = KIND_MAPPING[kind];
    const filtered = filterTransactionsForReport(transactions, kind, filters);

    const rows: ReportRow[] = filtered
      .map((t: FinanceTransaction) => {
        const account = accounts.find((a) => a.id === t.accountId);
        const category = categories.find((c) => c.id === t.categoryId);
        const costCenter = t.costCenterId
          ? costCenters.find((cc) => cc.id === t.costCenterId)
          : undefined;
        const hasCcSplit = !!t.costCenterSplits && t.costCenterSplits.length > 0;
        const counterparty = t.counterpartyId
          ? counterparties.find((cp) => cp.id === t.counterpartyId)
          : undefined;
        return {
          ...t,
          accountName: account?.name ?? '—',
          categoryName: category?.name ?? '—',
          ...(hasCcSplit
            ? { costCenterName: `Múltiplos (${t.costCenterSplits!.length})` }
            : costCenter
              ? { costCenterName: costCenter.name }
              : {}),
          ...(counterparty && { counterpartyName: counterparty.name }),
        };
      })
      .sort((a, b) => {
        const aDate = mapping.dateField === 'paymentDate' ? a.paymentDate : a.dueDate;
        const bDate = mapping.dateField === 'paymentDate' ? b.paymentDate : b.dueDate;
        return (aDate?.getTime() ?? 0) - (bDate?.getTime() ?? 0);
      });

    const summary = summarizeRows(filtered);
    return { rows, ...summary };
  }, [transactions, accounts, categories, costCenters, counterparties, kind, filters]);
}
